import { memo, useEffect, useRef, useState } from "react";
import { useStore } from "../store/globalState";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  usePreventReload,
  useKeyboardVisible,
} from "lola-framework-ui-test/src/hooks";
import {
  AuraLayout,
  Button,
  Close,
  LabelInput,
  Layout,
  Navbar,
  useBlockScroll,
} from "lola-framework-ui-test";
import { APP_ROUTES, flavorClassName, US_STATES } from "../constants";
import { COUNTRIES } from "../constants/countries";
import { useSessionStore } from "../store/useSessionStore";
import type { CardStepFields } from "./AddCardPage";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { useClaimLink } from "../hooks/useClaimLink";
import { safeCapture } from "../utils/posthog";
import { RainbowWrapper } from "../components/RainbowWrapper";
import { NavbarBackChevron } from "../components/NavbarBackChevron";
import "../styles/cardpage.style.css";

export interface AddressFormFields {
  street1: string;
  street2: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

interface AddressFormErrors {
  street1?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
}

export interface AddCardFullState {
  card?: CardStepFields;
  address: AddressFormFields;
}

export const AddCardAddressPage = memo(() => {
  useClaimLink();
  const { theme } = useStore();
  const { session } = useSessionStore();
  const { redirectTo } = useRouteWithToken();
  const location = useLocation();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isUSOnly =
    (session.data?.config?.cardOriginPolicy ?? "us_only") === "us_only";

  const cardData = (location.state as { card?: CardStepFields })?.card;

  useEffect(() => {
    if (!cardData) {
      redirectTo(APP_ROUTES.ADD_CARD_PAGE, { replace: true });
    }
  }, [cardData, redirectTo]);

  const [form, setForm] = useState<AddressFormFields>({
    street1: "",
    street2: "",
    city: "",
    state: "",
    zipCode: "",
    country: isUSOnly ? "US" : "",
  });
  const [errors, setErrors] = useState<AddressFormErrors>({});
  const [focusedField, setFocusedField] = useState<
    keyof AddressFormFields | null
  >(null);

  const viewedRef = useRef(false);
  useEffect(() => {
    if (!viewedRef.current) {
      viewedRef.current = true;
      safeCapture("ui.send.add_card_address.screen.view");
    }
  }, []);

  const isUS = form.country === "US";

  useBlockScroll();

  // Keeps the kit's mobile keyboard handling (body scroll lock on focus).
  // Height is owned by the kit Layout, so viewportHeight is not read here.
  useKeyboardVisible();

  usePreventReload();

  const colorsConfig = theme?.whiteView;
  const {
    backgroundBtn,
    textColorBtn,
    title,
    buttonShowIcon,
    buttonSize,
  } = colorsConfig;

  const handleChange = (field: keyof AddressFormFields, value: string) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "country" && value !== prev.country) {
        next.state = "";
      }
      return next;
    });
    if (errors[field as keyof AddressFormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = (): AddressFormErrors => {
    const errs: AddressFormErrors = {};
    if (!form.street1.trim())
      errs.street1 = t("billing.streetRequired", {
        defaultValue: "Street address is required",
      });
    if (!form.city.trim())
      errs.city = t("billing.cityRequired", {
        defaultValue: "City is required",
      });
    if (!form.state.trim())
      errs.state = t("billing.stateRequired", {
        defaultValue: "State is required",
      });

    if (isUS) {
      if (!/^\d{5}(-\d{4})?$/.test(form.zipCode))
        errs.zipCode = t("billing.zipRequired", {
          defaultValue: "Valid zip code is required",
        });
    } else {
      if (!form.zipCode.trim() || form.zipCode.trim().length < 2)
        errs.zipCode = t("billing.postalCodeRequired", {
          defaultValue: "Valid postal code is required",
        });
    }

    if (!isUSOnly && !form.country) {
      errs.country = t("billing.countryRequired", {
        defaultValue: "Country is required",
      });
    }

    return errs;
  };

  const handleContinue = () => {
    safeCapture("ui.send.add_card_address.continue_button.click");
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      safeCapture("ux.send.add_card_address.form.error", {
        success: false,
        error_code: "ADDRESS_VALIDATION",
        fields: Object.keys(validationErrors),
      });
      return;
    }

    redirectTo(APP_ROUTES.ADD_CARD_PROCESSING_PAGE, {
      state: { card: cardData, address: form } satisfies AddCardFullState,
    });
  };

  if (!cardData) return null;

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "12px 14px",
    borderRadius: "var(--lola-style-input-border-radius, 10px)",
    fontSize: "16px",
    background: "transparent",
    outline: "none",
    boxSizing: "border-box",
  };

  const errorStyle: React.CSSProperties = {
    color: "#dc251c",
    fontSize: "12px",
    marginTop: "4px",
  };

  // LabelInput applies `background`/`color` props AFTER labelStyle — must set them
  // explicitly or the kit defaults (#fff) create a "tape". Match the input fill
  // (--gradient-card), not the page background.
  const inputLabelBg = "var(--gradient-card)";
  const inputLabelColor = "var(--foreground)";

  return (
    <AuraLayout
      colorConfig={colorsConfig}
      className={`${flavorClassName} scrollable`}
    >
      <Layout.Header>
        <NavbarBackChevron onBackClick={() => navigate(-1)}>
          <Navbar
            className="legal-navbar"
            align="center"
            color={title}
            title={t("billing.title", { defaultValue: "Billing Address" })}
            onBackClick={() => navigate(-1)}
            icon={<Close size={20} colors={[title, title]} />}
            onIconClick={() => redirectTo(APP_ROUTES.TRANSFER_PAGE)}
          />
        </NavbarBackChevron>
      </Layout.Header>

      {/* The kit Layout already sizes the card from visualViewport; forcing
          Content to the full viewport height pushed the footer out of the
          card (visible in Safari). Let Content take the remaining space and
          scroll, with the footer pinned (see `.scrollable` in index.css). */}
      <Layout.Content isOverflowauto>
        <div className="cardpage-content">
          <div className="input-container-wrapper">
            <div className="input-field-shell">
              <LabelInput
                isActive={
                  focusedField === "street1" ||
                  !!form.street1 ||
                  !!errors.street1
                }
                background={inputLabelBg}
                color={inputLabelColor}
              >
                {t("billing.street1", {
                  defaultValue: "Street Address",
                })}
              </LabelInput>
              <RainbowWrapper
                value={form.street1}
                error={errors.street1}
                isActive={focusedField === "street1"}
              >
                <input
                  type="text"
                  aria-label={t("billing.street1", {
                    defaultValue: "Street Address",
                  })}
                  className="input-style"
                  value={form.street1}
                  onChange={(e) => handleChange("street1", e.target.value)}
                  onFocus={() => setFocusedField("street1")}
                  onBlur={() => setFocusedField(null)}
                  style={inputStyle}
                  autoFocus
                />
              </RainbowWrapper>
            </div>
            {errors.street1 && <div style={errorStyle}>{errors.street1}</div>}
          </div>

          <div className="input-container-wrapper">
            <div className="input-field-shell">
              <LabelInput
                isActive={focusedField === "street2" || !!form.street2}
                background={inputLabelBg}
                color={inputLabelColor}
              >
                {t("billing.street2", {
                  defaultValue: "Apt, Suite, Unit (optional)",
                })}
              </LabelInput>
              <RainbowWrapper
                value={form.street2}
                isActive={focusedField === "street2"}
              >
                <input
                  type="text"
                  aria-label={t("billing.street2", {
                    defaultValue: "Apt, Suite, Unit (optional)",
                  })}
                  className="input-style"
                  value={form.street2}
                  onChange={(e) => handleChange("street2", e.target.value)}
                  onFocus={() => setFocusedField("street2")}
                  onBlur={() => setFocusedField(null)}
                  style={inputStyle}
                />
              </RainbowWrapper>
            </div>
          </div>

          <div className="cardpage-row">
            <div className="input-container-wrapper cardpage-row__field">
              <div className="input-field-shell">
                <LabelInput
                  isActive={
                    focusedField === "city" || !!form.city || !!errors.city
                  }
                  background={inputLabelBg}
                  color={inputLabelColor}
                >
                  {t("billing.city", { defaultValue: "City" })}
                </LabelInput>
                <RainbowWrapper
                  value={form.city}
                  error={errors.city}
                  isActive={focusedField === "city"}
                >
                  <input
                    type="text"
                    className="input-style"
                    aria-label={t("billing.city", { defaultValue: "City" })}
                    value={form.city}
                    onChange={(e) => handleChange("city", e.target.value)}
                    onFocus={() => setFocusedField("city")}
                    onBlur={() => setFocusedField(null)}
                    style={inputStyle}
                  />
                </RainbowWrapper>
              </div>
              {errors.city && <div style={errorStyle}>{errors.city}</div>}
            </div>
            <div className="input-container-wrapper cardpage-row__field">
              <div className="input-field-shell">
                <LabelInput
                  isActive={
                    focusedField === "state" || !!form.state || !!errors.state
                  }
                  background={inputLabelBg}
                  color={inputLabelColor}
                >
                  {focusedField === "state" || !!form.state || !!errors.state
                    ? t("billing.state", { defaultValue: "State" })
                    : ""}
                </LabelInput>
                <RainbowWrapper
                  value={form.state}
                  error={errors.state}
                  isActive={focusedField === "state"}
                >
                  <select
                    required
                    value={form.state}
                    className={`input-style select-input${!form.state ? " select-input--empty" : ""}`}
                    onChange={(e) => handleChange("state", e.target.value)}
                    onFocus={() => setFocusedField("state")}
                    onBlur={() => setFocusedField(null)}
                    style={inputStyle}
                    aria-label={t("billing.state", { defaultValue: "State" })}
                  >
                    <option value="" disabled>
                      {t("billing.selectState", { defaultValue: "State" })}
                    </option>
                    {US_STATES.map((s) => (
                      <option key={s.code} value={s.code}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </RainbowWrapper>
              </div>
              {errors.state && <div style={errorStyle}>{errors.state}</div>}
            </div>
          </div>

          <div className="cardpage-row">
            <div className="input-container-wrapper cardpage-row__field">
              <div className="input-field-shell">
                <LabelInput
                  isActive={
                    focusedField === "zipCode" ||
                    !!form.zipCode ||
                    !!errors.zipCode
                  }
                  background={inputLabelBg}
                  color={inputLabelColor}
                >
                  {t("billing.zipCode", { defaultValue: "Zip Code" })}
                </LabelInput>
                <RainbowWrapper
                  value={form.zipCode}
                  error={errors.zipCode}
                  isActive={focusedField === "zipCode"}
                >
                  <input
                    type="text"
                    inputMode="numeric"
                    className="input-style"
                    aria-label={t("billing.zipCode", {
                      defaultValue: "Zip Code",
                    })}
                    value={form.zipCode}
                    onChange={(e) =>
                      handleChange(
                        "zipCode",
                        e.target.value.replace(/[^\d-]/g, "")
                      )
                    }
                    onFocus={() => setFocusedField("zipCode")}
                    onBlur={() => setFocusedField(null)}
                    maxLength={10}
                    style={inputStyle}
                  />
                </RainbowWrapper>
              </div>
              {errors.zipCode && <div style={errorStyle}>{errors.zipCode}</div>}
            </div>
            <div className="input-container-wrapper cardpage-row__field">
              <div className="input-field-shell">
                <LabelInput
                  isActive
                  background={inputLabelBg}
                  color={inputLabelColor}
                >
                  {t("billing.country", { defaultValue: "Country" })}
                </LabelInput>
                {isUSOnly ? (
                  <RainbowWrapper value="US" isActive={false}>
                    <input
                      type="text"
                      className="input-style input-style--readonly"
                      value="United States"
                      readOnly
                      disabled
                      tabIndex={-1}
                      aria-readonly="true"
                      style={{
                        ...inputStyle,
                        cursor: "not-allowed",
                      }}
                    />
                  </RainbowWrapper>
                ) : (
                  <RainbowWrapper
                    value={form.country}
                    error={errors.country}
                    isActive={focusedField === "country" || !!form.country}
                  >
                    <select
                      value={form.country}
                      className={`input-style select-input${!form.country ? " select-input--empty" : ""}`}
                      onChange={(e) =>
                        handleChange("country", e.target.value)
                      }
                      onFocus={() => setFocusedField("country")}
                      onBlur={() => setFocusedField(null)}
                      style={inputStyle}
                      aria-label={t("billing.country", {
                        defaultValue: "Country",
                      })}
                    >
                      <option value="" disabled>
                        {t("billing.selectCountry", {
                          defaultValue: "Select country",
                        })}
                      </option>
                      {COUNTRIES.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </RainbowWrapper>
                )}
              </div>
              {!isUSOnly && errors.country && (
                <div style={errorStyle}>{errors.country}</div>
              )}
            </div>
          </div>
        </div>
      </Layout.Content>
      <Layout.Footer>
        <Button
          color={textColorBtn}
          background={backgroundBtn}
          showIcon={buttonShowIcon}
          size={buttonSize ?? "small"}
          onClick={handleContinue}
        >
          {t("card.continue")}
        </Button>
      </Layout.Footer>
    </AuraLayout>
  );
});
